import React, { useCallback, useState } from "react";

import CustomButton from "components/common/Button";
import InlineLoader from "./InlineLoader";
import { listBillingAuditTrail } from "services/billing";
import type { BsPaginated } from "services/billing";

import { useCursorList } from "./useCursorList";
import { DatePicker, TableComponent } from "./ui";
import { formatDate, titleCase } from "./types";

type AuditRow = Record<string, unknown>;

const columns = [
  { id: "createdAt", label: "When", render: (v: string) => formatDate(v) },
  { id: "adminUserId", label: "Admin", render: (v: string) => v || "—" },
  { id: "action", label: "Action", render: (v: string) => titleCase(v) },
  { id: "entityType", label: "Entity", render: (v: string) => titleCase(v) },
  { id: "entityId", label: "Entity ID", render: (v: string) => v || "—" },
];

// Widen a picked day to a full-day bound so "to" stays inclusive of the day
// regardless of the repo's lt/lte semantics.
const startOfDayIso = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).toISOString();
const endOfDayIso = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).toISOString();

export default function AuditTab() {
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);
  // Only re-fetch when the user applies — not on every picker change.
  const [applied, setApplied] = useState<{ dateFrom?: string; dateTo?: string }>({});

  const load = useCallback(
    (cursor?: string) =>
      listBillingAuditTrail({
        dateFrom: applied.dateFrom,
        dateTo: applied.dateTo,
        cursor,
        limit: 25,
      }) as Promise<BsPaginated<AuditRow>>,
    [applied]
  );
  const { rows, hasMore, loading, loadingMore, error, loadMore } = useCursorList<AuditRow>(load);

  const apply = () =>
    setApplied({
      dateFrom: from ? startOfDayIso(from) : undefined,
      dateTo: to ? endOfDayIso(to) : undefined,
    });
  const clear = () => {
    setFrom(null);
    setTo(null);
    setApplied({});
  };

  const hasFilter = Boolean(applied.dateFrom || applied.dateTo);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-05 items-end" style={{ flexWrap: "wrap" }}>
        <div style={{ width: 200 }}>
          <DatePicker
            name="audit-from"
            label="From"
            value={from}
            onChange={setFrom}
            maxDate={to || undefined}
            placeholder="From date"
          />
        </div>
        <div style={{ width: 200 }}>
          <DatePicker
            name="audit-to"
            label="To"
            value={to}
            onChange={setTo}
            minDate={from || undefined}
            placeholder="To date"
          />
        </div>
        <CustomButton label="Apply" onClick={apply} disabled={!from && !to} />
        {hasFilter && <CustomButton label="Clear" onClick={clear} />}
      </div>

      {loading ? (
        <InlineLoader />
      ) : error ? (
        <p className="para-body-m-regular color-text-body-secondary">{error}</p>
      ) : (
        <>
          <TableComponent columns={columns} data={rows} disableSort />
          {hasMore && (
            <div className="flex justify-center mt-05">
              <CustomButton label={loadingMore ? "Loading…" : "Load more"} onClick={loadMore} disabled={loadingMore} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
